import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { v4 } from 'uuid';
import { useAuth } from '../contexts/AuthContext';
import { 
  Paper, 
  Box, 
  Typography, 
  TextField, 
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Send as SendIcon, 
  SmartToy as BotIcon, 
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon 
} from '@mui/icons-material';
import './OSCAR.css';
import logger from '../utils/logger';
import { apiGet, apiPost } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const OSCAR = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Only initialize when auth is complete and we have a user
    if (authLoading || !currentUser?.email) return;

    // Check if we already have a session to prevent double initialization
    if (sessionRef.current) return;

    // Generate session ID on first load
    const newSessionId = v4();
    sessionRef.current = newSessionId;
    
    // Set user in logger
    logger.setUser(currentUser);
    
    logger.info('OSCAR session initialized', {
      component: 'OSCAR',
      sessionId: newSessionId,
      userEmail: currentUser.email
    });
    
    fetchRecordings();

    // Clean up session on unmount (logout)
    return () => {
      if (sessionRef.current) {
        logger.info('OSCAR session ended', {
          component: 'OSCAR',
          sessionId: sessionRef.current,
          userEmail: currentUser.email
        });
        logger.setUser(null);
        sessionRef.current = null;
      }
    };
  }, [authLoading, currentUser]);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      logger.info('Fetching recordings', {
        component: 'OSCAR',
        sessionId: sessionRef.current
      });

      const data = await apiGet('/files/withMetadata');
      
      // Transform files into select options, only include completed transcriptions
      const options = data.files
        .filter(file => file.metadata.transcribeStatus === 'completed')
        .sort((a, b) => new Date(b.metadata.uploadTimestamp) - new Date(a.metadata.uploadTimestamp))
        .map(file => {
          const uniqueId = file.metadata.uniqueId;
          return {
            value: file.key,
            label: file.filename,
            data: {
              ...file,
              uniqueId: uniqueId
            }
          };
        });

      setRecordings(options);
      setError(null);
    } catch (err) {
      logger.error('Error fetching recordings:', err, {
        component: 'OSCAR',
        sessionId: sessionRef.current
      });
      setError('Failed to fetch recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyMessage = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      
      logger.debug('Message copied to clipboard', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        messageId,
        action: 'copy_message'
      });

      setSnackbar({ open: true, message: 'Message copied to clipboard!', severity: 'success' });
      
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      logger.error('Failed to copy message', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        messageId,
        error: err.message
      });
      setSnackbar({ open: true, message: 'Failed to copy message', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    logger.debug('Snackbar closed', {
      component: 'OSCAR',
      sessionId: sessionRef.current,
      action: 'close_snackbar'
    });
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRecordingSelect = async (selected) => {
    logger.info('Recording selected', {
      component: 'OSCAR',
      sessionId: sessionRef.current,
      recordingId: selected?.value,
      action: 'select_recording'
    });
    setSelectedRecording(selected);
  };

  const handleSend = async () => {
    if (!inputText.trim() || !selectedRecording || sending) return;

    const meetingId = selectedRecording.data.metadata.uniqueId;
    const meetingName = selectedRecording.label;
    const userEmail = currentUser?.email;

    logger.debug('User sending message', {
      component: 'OSCAR',
      sessionId: sessionRef.current,
      meetingId,
      action: 'send_message'
    });

    const prefixedInput = inputText;

    // Add user message
    const userMessage = {
      text: prefixedInput,
      sender: 'user',
      timestamp: new Date().toISOString(),
      metadata: {
        meetingId,
        meetingName,
        userEmail
      }
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // Log user message
    logger.info(`OSCAR request: from ${currentUser?.email}:`, {
      component: 'OSCAR',
      sessionId: sessionRef.current,
      messageType: 'user',
      message: prefixedInput,
      meetingId,
      meetingName,
      userEmail: currentUser?.email
    });
    
    setInputText('');
    setSending(true);

    try {
      logger.debug('Invoking Bedrock agent', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        meetingId,
        meetingName
      });

      const data = await apiPost('/bedrockagent/invoke', {
        input: prefixedInput,
        sessionId: sessionRef.current,
        meetingId: meetingId,
        meetingName: meetingName,
        userEmail: userEmail
      });

      logger.debug('Received Bedrock agent response', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        meetingId,
        responseLength: data.content?.length
      });
      
      // Add bot message
      const botMessage = {
        text: data.content || 'Sorry, I could not process your request..',
        sender: 'bot',
        timestamp: new Date().toISOString(),
        metadata: {
          meetingId,
          meetingName
        }
      };
      
      // Log bot response
      logger.info('OSCAR response: ', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        messageType: 'bot',
        message: botMessage.text,
        meetingId,
        meetingName,
        userEmail: currentUser?.email
      });
      
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      logger.error('Failed to process message', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        meetingId,
        error: error.message
      });

      // Add error message
      const errorMessage = {
        text: `Sorry, there was an error processing your request. ${error.response?.data?.errorName ? `\nError: ${error.response.data.errorName}` : ''} ${error.response?.data?.errorDetails ? `\nDetails: ${error.response.data.errorDetails}` : ''} ${error.response?.data?.errorStack ? `\nStack: ${error.response.data.errorStack.split('\n')[0]}` : ''}`,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true,
        metadata: {
          meetingId,
          meetingName,
          errorDetails: {
            name: error.response?.data?.errorName,
            message: error.response?.data?.errorDetails,
            stack: error.response?.data?.errorStack?.split('\n')[0]
          }
        }
      };
      
      // Log error response
      logger.error('Bot error response', {
        component: 'OSCAR',
        sessionId: sessionRef.current,
        messageType: 'bot_error',
        message: errorMessage.text,
        meetingId,
        meetingName,
        userEmail: currentUser?.email,
        error: error.message
      });
      
      setMessages(prev => [...prev, errorMessage]);
      setSnackbar({
        open: true,
        message: 'Failed to process message',
        severity: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      p: 2,
      gap: 2
    }}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Select
            className="recording-select"
            classNamePrefix="recording-select"
            value={selectedRecording}
            onChange={handleRecordingSelect}
            options={recordings}
            isLoading={loading}
            placeholder={`Select a recording... (${recordings.length} available)`}
            isClearable
            maxMenuHeight={300}
            menuPlacement="auto"
            menuShouldScrollIntoView={true}
            isSearchable={true}
          />
          <IconButton 
            onClick={fetchRecordings} 
            disabled={loading}
            color="primary"
            title="Refresh recordings list"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      <Paper 
        elevation={2} 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {messages.length === 0 ? (
            <div className="messages-placeholder">
              Start a conversation by asking a question about the selected meeting recording
              <p>For example, "How many people participated in this meeting?"</p>
              <p> "Create meeting notes"</p>
              <p> "What are the main topics of discussion in this meeting?"</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                  gap: 1,
                  alignItems: 'flex-start'
                }}
              >
                {message.sender === 'bot' ? (
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <BotIcon />
                  </Avatar>
                ) : (
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {currentUser?.email[0].toUpperCase()}
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '70%',
                    minWidth: '100px',
                    position: 'relative'
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: message.sender === 'user' ? 'primary.light' : 'background.paper',
                      borderRadius: 2,
                      ...(message.isError && {
                        bgcolor: 'error.light',
                        color: 'error.contrastText'
                      }),
                      '&:hover .copy-button': {
                        opacity: 1
                      }
                    }}
                  >
                    <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.text}
                    </Typography>
                    <IconButton
                      className="copy-button"
                      onClick={() => handleCopyMessage(message.text, index)}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      {copiedMessageId === index ? <CheckIcon color="success" /> : <CopyIcon />}
                    </IconButton>
                  </Paper>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      textAlign: message.sender === 'user' ? 'right' : 'left',
                      color: 'text.secondary'
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask your questions here..."
              disabled={!selectedRecording}
              variant="outlined"
              size="small"
            />
            <IconButton 
              color="primary"
              onClick={handleSend}
              disabled={!inputText.trim() || !selectedRecording || sending}
              sx={{ p: 1 }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
          {selectedRecording && (
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Selected: ${selectedRecording.label}`}
                onDelete={() => setSelectedRecording(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
      </Paper>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OSCAR;
